import Link from '@/components/Link';
import { apiCap } from '@gik/api';
import { useTaxonomies } from '@gik/api/give/taxonomies';
import { timeoutDefaultValue } from '@gik/core/constants';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/CustomHooks';
import type { WordpressTaxonomy } from '@gik/core/models/wordpress/WordpressTaxonomy';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { scrollIntoView } from '@gik/core/utils/scroll';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import type { GiveTaxonomyType } from '@gik/shop/types/GiveTaxonomyType';
import { Badge } from '@gik/ui/Badge';
import { ApiErrorDisplay } from '@gik/ui/gik/ApiErrorDisplay';
import { Grid } from '@gik/ui/Grid';
import { HorizontalScroll } from '@gik/ui/HorizontalScroll';
import { HTMLParser } from '@gik/ui/HTMLParser';
import { Image } from '@gik/ui/Image';
import { LoadingSpinner } from '@gik/ui/LoadingSpinner';
import { ButtonBone } from '@gik/ui/SkeletonLoader';
import React from 'react';

export type TaxonomyNavProps = {
  taxonomyType?: GiveTaxonomyType;
  parentSlug?: string;
  childSlug?: string;
  selected?: string;
  // ssr initial data
  items?: WordpressTaxonomy[];
  skeletonItems?: number;
  onChange?(slug: string): void;
} & UIComponent;

export type TaxonomyNavItem = {
  id: number;
  slug: string;
  parent?: WordpressTaxonomy;
  icon: string;
  title: string;
};

function TaxonomyNavComp(props: TaxonomyNavProps): React.ReactElement {
  const bem = useBemCN('taxonomy-nav');
  const isSmUp = useBreakpoint(Breakpoint.SM);

  const mainRef = React.useRef<HTMLDivElement>();
  const { taxonomyType, selected } = props;

  const { data: taxonomies, error: taxonomiesError } = useTaxonomies(
    taxonomyType,
    { page: 1, perPage: apiCap },
    {
      fallbackData: props.items,
    }
  );

  function scrollSelectedIntoView() {
    const el = mainRef.current.querySelector('.gik-taxonomy-nav__item--selected') as HTMLElement;
    const nav = mainRef.current.querySelector('.gik-horizontal-nav__scroll-container') as HTMLElement;
    if (el && nav) {
      scrollIntoView(el, {
        container: nav,
        offset: -nav.offsetWidth / 2 + el.offsetWidth / 2,
      });
    }
  }

  React.useEffect(() => {
    // scroll the selected item into view
    setTimeout(scrollSelectedIntoView, timeoutDefaultValue);
  }, [selected]);

  const isLoading = !taxonomies;

  if (isLoading) return <LoadingSpinner />;
  if (taxonomiesError) return <ApiErrorDisplay />;

  let items: TaxonomyNavItem[] = taxonomies
    ?.filter(item => item.slug !== 'uncategorized')
    // ?.sort((a, b) => parseInt(a.acf.weighting) - parseInt(b.acf.weighting))
    .map(item => ({
      id: item.id,
      slug: item.slug,
      parent: taxonomies.find(i => i.id === item.parent),
      icon: item.acf.taxonomy_svg_icon || item.acf.tax_thumb_image,
      title: item.name,
    }));

  if (taxonomyType === 'situation') {
    items = items.filter(item => item.parent !== undefined);
  }

  function getLink(item: TaxonomyNavItem) {
    if (taxonomyType === 'situation' && item.parent) {
      return `/give/by/${taxonomyType}/${item.parent?.slug}/${item.slug}`;
    }
    return `/give/by/${taxonomyType}/${item.slug}`;
  }

  return (
    <div {...bem(null, [{ [`type-${taxonomyType}`]: taxonomyType }])} ref={mainRef}>
      <HorizontalScroll fadeSides arrows={isSmUp} scrollbar={!isSmUp}>
        <Grid>
          {items.map(item => {
            return (
              <Link key={item.id} href={getLink(item)}>
                <Badge
                  pill
                  variant="default-light"
                  key={item.id}
                  {...bem('item', [{ selected: item.slug === selected }])}
                >
                  <Image src={item.icon} width={30} height={30} />
                  <span>
                    <HTMLParser rawHtml={item.title} />
                  </span>
                </Badge>
              </Link>
            );
          })}
        </Grid>
      </HorizontalScroll>
    </div>
  );
}

export const TaxonomyNav = React.memo(withComponentErrorBoundary(TaxonomyNavComp));

export const TaxonomyNavSkeleton = ({ skeletonItems = 20 }: Partial<TaxonomyNavProps>): React.ReactElement => {
  const bem = useBemCN(`taxonomy-nav-skeleton`);

  return (
    <div {...bem()}>
      {Array.from(Array(skeletonItems).keys()).map(index => {
        return <ButtonBone pill key={index} />;
      })}
    </div>
  );
};
